import React, { memo, useCallback, useMemo } from 'react'

import { useAppDispatch } from 'hooks/reduxHooks'
import styled from 'styled-components'

import { OfferPageActions, OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { getTourV2OfferPageBaseUrl } from 'selectors/TourV2/tourV2OfferPageSelectors'
import { pushWithRegion } from 'actions/NavigationActions'
import { sortBy } from 'lib/array/arrayUtils'
import findCheapestTourV2PurchasableOption from 'lib/tours/findCheapestTourV2PurchasableOption'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'

import TourV2VariationCard from './TourV2VariationCard'
import { tourV2OfferVariationView } from 'actions/AnalyticsActions'
import CardCarousel from 'components/Luxkit/Carousel/CardCarousel'
import { rem } from 'polished'
import { EmptyObject } from 'lib/object/objectUtils'

const VariationsList = styled.div``

interface MappedProps {
  offerPageState: OfferPageState
  baseTourOfferUrl: string
  offer: App.Tours.TourV2Offer
  selectedVariation: App.Tours.TourV2OfferVariation
  variations: Record<string, App.Tours.TourV2OfferVariation>
}

interface Props {
  onVariationChanged: (variationId: string) => void
}

function TourV2Variations(props: Props & MappedProps) {
  const {
    baseTourOfferUrl,
    offer,
    selectedVariation,
    variations,
    onVariationChanged,
  } = props

  const offerPageDispatch = useOfferPageDispatch()

  const dispatch = useAppDispatch()

  const selectVariationId = useCallback(
    (variationId: App.Tours.TourV2OfferVariation['id']) => {
      if (selectedVariation.id !== variationId) {
        offerPageDispatch({
          type: OfferPageActions.setTourV2Departure,
          departure: undefined,
        })
        dispatch(pushWithRegion(
          `${baseTourOfferUrl}/${variationId}`,
          '',
          { scrollBehaviour: 'smooth', scrollTo: '#TOUR_V2_ITINERARY_TOP_ANCHOR' },
        ))
        dispatch(tourV2OfferVariationView(offer))
        offerPageDispatch({
          type: OfferPageActions.setTourV2Variation,
          variation: offer.variations[variationId],
        })
        onVariationChanged(variationId)
      }
    },

    [selectedVariation, offerPageDispatch, dispatch, baseTourOfferUrl, onVariationChanged, offer],
  )

  const sortedVariations = useMemo(() => {
    return sortBy(
      Object.values(variations),
      (variation) => findCheapestTourV2PurchasableOption(offer, variation.id)?.price
      , 'asc',
    )
  }, [offer, variations])

  return <VariationsList>
    <CardCarousel width={`min(${rem(460)}, 85%)`} tabletWidth={`min(${rem(460)}, 75%)`}>
      {sortedVariations.map((variationOption, variationOptionIndex) =>
        <TourV2VariationCard
          key={variationOptionIndex + '_1'}
          position={variationOptionIndex + 1}
          offer={offer}
          variation={variationOption}
          isSelected={variationOption.id === selectedVariation.id}
          onVariationSelect={selectVariationId}
        />)
      }
    </CardCarousel>
  </VariationsList>
}

const mapOfferPageStateToProps = (offerPageState: OfferPageState): MappedProps => ({
  offerPageState,
  baseTourOfferUrl: getTourV2OfferPageBaseUrl(offerPageState),
  offer: offerPageState.tourV2Offer!,
  selectedVariation: offerPageState.tourV2Variation!,
  variations: offerPageState.tourV2Offer?.variations ?? EmptyObject,
})

export default memo(withOfferPageState(mapOfferPageStateToProps, TourV2Variations))
